import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { Goal, GoalStatus, GoalHabit, HabitFrequency, GoalFormData, GoalCategory, GoalProgressHistory } from '../../types/Goal';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { goalService } from '../../services/goalService';
import { relationshipService } from '../../services/relationshipService';
import { habitService, Habit } from '../../services/habitService';
import { taskService, Task, TaskFormData } from '../../services/taskService';
import { TimeLogsService, TimeLog, CreateTimeLogData } from '../../services/timelogsService';
import { ObjectType } from '../../types/Relationship';
import { NotesService, Note } from '../../services/notesService';
import { metricService, Metric, MetricValue } from '../../services/metricService';
import BlockEditor from '../notes/BlockEditor';
import { useBlockManager } from '../../hooks/useBlockManager';
import { BlockContainer } from '../../services/blockManager';
import GoalList from './GoalList';
import Sidebar from '../Sidebar';
import './GoalDetail.css';
import './GoalList.css';

interface GoalDetailProps {
  user: User | null;
  goal?: Goal; // Optional when used as routed page
  onClose?: () => void; // Optional when used as routed page
  onGoalUpdate?: (updatedGoal: Goal) => void; // Optional when used as routed page
  onGoalEdit?: (goal: Goal) => void;
  onGoalDelete?: (goalId: string) => void;
  // Props for when used as routed page
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onSignOut?: () => void;
  onShowAuthModal?: () => void;
  authLoading?: boolean;
}

const GoalDetail: React.FC<GoalDetailProps> = ({ 
  user, 
  goal: propGoal, 
  onClose, 
  onGoalUpdate, 
  onGoalEdit, 
  onGoalDelete,
  sidebarOpen = false,
  onToggleSidebar,
  onSignOut,
  onShowAuthModal,
  authLoading = false
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isRoutedPage = Boolean(id && !propGoal); // Determine if used as routed page
  
  const [goal, setGoal] = useState<Goal | null>(propGoal || null);
  const [activeTab, setActiveTab] = useState<'overview' | 'habits' | 'tasks' | 'notes' | 'subgoals' | 'history'>('overview');
  const [loading, setLoading] = useState(isRoutedPage);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedSubGoal, setSelectedSubGoal] = useState<Goal | null>(null);
  const [showSubGoalDetail, setShowSubGoalDetail] = useState(false);

  // Options menu state
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  
  // Collapsible sections state
  const [showProgressBreakdown, setShowProgressBreakdown] = useState(false);
  const [showDetailsSection, setShowDetailsSection] = useState(false);


  // Habits state
  const [goalHabits, setGoalHabits] = useState<GoalHabit[]>([]);
  const [linkedHabits, setLinkedHabits] = useState<Habit[]>([]);
  const [availableHabits, setAvailableHabits] = useState<Habit[]>([]);
  const [showHabitForm, setShowHabitForm] = useState(false);
  const [showLinkHabitModal, setShowLinkHabitModal] = useState(false);
  const [selectedHabitIds, setSelectedHabitIds] = useState<number[]>([]);
  const [habitSearchTerm, setHabitSearchTerm] = useState('');
  const [habitFormData, setHabitFormData] = useState({
    title: '',
    description: '',
    frequency: HabitFrequency.DAILY,
    targetCount: 1
  });

  // Subgoals state
  const [subGoals, setSubGoals] = useState<Goal[]>([]);
  const [showSubGoalForm, setShowSubGoalForm] = useState(false);
  const [showLinkSubGoalModal, setShowLinkSubGoalModal] = useState(false);
  const [availableGoals, setAvailableGoals] = useState<Goal[]>([]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [goalSearchTerm, setGoalSearchTerm] = useState('');
  const [subGoalFormData, setSubGoalFormData] = useState<GoalFormData>({
    title: '',
    description: '',
    category: GoalCategory.PERSONAL,
    targetDate: '',
    startedDate: '',
    notes: ''
  });

  // Tasks state
  const [goalTasks, setGoalTasks] = useState<Task[]>([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormData, setTaskFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    status: 'todo',
    due_date: '',
    attachments: []
  });

  // Time tracking state
  const [goalTimeLogs, setGoalTimeLogs] = useState<TimeLog[]>([]);
  const [showTimeLogForm, setShowTimeLogForm] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState<Date | null>(null);
  const [currentTimerDuration, setCurrentTimerDuration] = useState(0);
  const [timeLogStats, setTimeLogStats] = useState({
    totalMinutes: 0,
    totalHours: 0,
    sessionCount: 0,
    todayMinutes: 0,
    thisWeekMinutes: 0
  });
  const [timeLogFormData, setTimeLogFormData] = useState({
    duration_minutes: 30,
    description: '',
    start_time: '',
    end_time: ''
  });

  // Progress calculation state
  const [calculatedProgress, setCalculatedProgress] = useState({
    overall: 0,
    habitProgress: 0,
    taskProgress: 0,
    metricProgress: 0,
    basedOnMetric: false,
    basedOnSubgoals: false,
    metricDetails: null as { name: string; currentValue: number; targetValue: number; progress: number } | null,
    breakdown: {
      habits: { score: 0, weight: 0.5, contribution: 0, count: 0, details: [] as any[] },
      tasks: { score: 0, weight: 0.5, contribution: 0, count: 0, details: [] as any[] },
      metric: null as { score: number; weight: number; contribution: number; name: string; currentValue: number; targetValue: number } | null,
      subgoals: null as { score: number; weight: number; contribution: number; count: number; details: any[] } | null
    }
  });
  const [progressLoading, setProgressLoading] = useState(false);

  // Goal note state
  const [goalNote, setGoalNote] = useState<Note | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Metric state
  const [currentMetricValue, setCurrentMetricValue] = useState<MetricValue | null>(null);
  const [metricLoading, setMetricLoading] = useState(false);
  const [metricProgress, setMetricProgress] = useState<number>(0);
  const [availableMetrics, setAvailableMetrics] = useState<Metric[]>([]);

  // Progress history state
  const [progressHistory, setProgressHistory] = useState<GoalProgressHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Refs to prevent duplicate loading calls
  const loadingNoteRef = useRef<boolean>(false);
  
  // Create a ref to hold the current goal note for the callback
  const goalNoteRef = useRef<Note | null>(null);
  
  // Update the ref whenever goalNote changes
  useEffect(() => {
    goalNoteRef.current = goalNote;
  }, [goalNote]);

  // Block management for goal note
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loading: blocksLoading,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: user?.id || 0,
    containerType: 'note',
    containerId: goalNote?.note_id,
    onContainerUpdate: async (container: BlockContainer) => {
      // Use the ref to get the current value, not the captured closure value
      const goalNoteValue = goalNoteRef.current;
      
      if (goalNoteValue) {
        const blocks = container.blocks.map((blockId, index) => ({
          block_id: blockId,
          order: index
        }));

        await NotesService.updateNote(goalNoteValue.note_id, {
          title: goalNoteValue.title,
          blocks,
          properties: goalNoteValue.properties
        });
      }
    }
  });

  // Load or create goal note
  const loadGoalNote = useCallback(async () => {
    if (!user?.id || !goal || loadingNoteRef.current) return;

    loadingNoteRef.current = true;
    setNoteLoading(true);
    setError(null);

    try {
      const userId = user.id;
      const goalNoteId = `goal-${goal.id}`;
      const goalNoteTitle = `Goal: ${goal.title}`;

      try {
        // Try to load existing note
        const existingNote = await NotesService.getNoteById(goalNoteId, userId);
        setGoalNote(existingNote);
        setContainerId(existingNote.note_id);

        // Load the content blocks
        const blockIds = existingNote.blocks.map(b => b.block_id);
        await loadBlocks(blockIds);
      } catch (error: any) {
        // If note doesn't exist, create it
        if (error.status === 404 || error.message?.includes('not found')) {
          const newNote = await NotesService.createNoteWithId(
            userId,
            goalNoteId,
            goalNoteTitle,
            [],
            { goalId: goal.id, type: 'goal-note' }
          );
          
          setGoalNote(newNote);
          setContainerId(newNote.note_id);
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      console.error('Error loading goal note:', error);
      setError('Failed to load goal note');
    } finally {
      loadingNoteRef.current = false;
      setNoteLoading(false);
    }
  }, [user?.id, goal, loadBlocks, setContainerId]);

  // Load goal from route params when used as routed page
  useEffect(() => {
    if (isRoutedPage && user && id) {
      loadGoalFromRoute();
    } else if (propGoal) {
      setGoal(propGoal);
    }
  }, [isRoutedPage, user, id, propGoal]);

  // Load progress history
  const loadProgressHistory = useCallback(async () => {
    if (!goal?.id || !user) return;
    
    try {
      setHistoryLoading(true);
      const response = await goalService.getGoalProgressHistory(goal.id, { limit: 90 });
      
      if (response.success && response.history) {
        setProgressHistory(response.history);
      }
    } catch (error) {
      console.error('Error loading progress history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [goal?.id, user]);

  // Load related data when goal changes
  useEffect(() => {
    if (goal && user) {
      loadSubGoals();
      loadLinkedHabits();
      loadAvailableHabits();
      loadLinkedTasks();
      loadGoalTimeLogs();
      loadGoalNote();
      loadMetricValue();
      loadAvailableMetrics();
      loadAvailableGoals();
      loadProgressHistory();
    }
  }, [goal?.id, user, loadGoalNote, loadProgressHistory]);


  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning && timerStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        const duration = Math.floor((now.getTime() - timerStartTime.getTime()) / 1000 / 60); // minutes
        setCurrentTimerDuration(duration);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, timerStartTime]);

  // Load goal from route (when used as routed page)
  const loadGoalFromRoute = async () => {
    if (!user || !id) return;
    
    setLoading(true);
    try {
      const response = await goalService.getGoalById(id);
      
      if (response.success && response.goal) {
        const convertedGoal = goalService.convertFirebaseGoal(response.goal);
        setGoal(convertedGoal);
        setError(null);
      } else {
        setError('Goal not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load goal');
      console.error('Error loading goal:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load habits linked to this goal via relationships
  const loadLinkedHabits = async () => {
    if (!user || !goal) return;
    
    try {
      const goalId = parseInt(goal.id);
      if (isNaN(goalId)) return;

      // Get relationships from this goal to habits
      const relationships = await relationshipService.getRelatedObjects(
        goalId.toString(),
        ObjectType.GOAL,
        ObjectType.HABIT,
        'from',
        50
      );

      // Fetch the actual habit data for linked habits
      const habitIds = relationships.fromObjects.map(obj => obj.id);
      const linkedHabitsData: Habit[] = [];
      
      for (const habitId of habitIds) {
        try {
          const response = await habitService.getHabitById(parseInt(habitId));
          if (response.success && response.habit) {
            linkedHabitsData.push(response.habit);
          }
        } catch (err) {
          console.warn(`Failed to fetch habit ${habitId}:`, err);
        }
      }
      
      setLinkedHabits(linkedHabitsData);
      
      // Recalculate progress after loading habits
      await calculateProgressFromBackend();
    } catch (error) {
      console.error('Error loading linked habits:', error);
    }
  };


  // Load all available habits for linking
  const loadAvailableHabits = async () => {
    if (!user) return;
    
    try {
      const response = await habitService.getUserHabits(user.id, {
        is_active: true,
        limit: 100
      });
      
      if (response.success && response.habits) {
        setAvailableHabits(response.habits);
      }
    } catch (error) {
      console.error('Error loading available habits:', error);
    }
  };

  // Load all available metrics
  const loadAvailableMetrics = async () => {
    try {
      const response = await metricService.getAllMetrics({ is_active: true });
      
      if (response.success && response.metrics) {
        setAvailableMetrics(response.metrics);
      }
    } catch (error) {
      console.error('Error loading available metrics:', error);
    }
  };

  // Load tasks linked to this goal via relationships
  const loadLinkedTasks = async () => {
    if (!user || !goal) return;
    
    try {
      const goalId = parseInt(goal.id);
      if (isNaN(goalId)) return;

      // Get relationships from this goal to tasks
      const relationships = await relationshipService.getRelatedObjects(
        goalId.toString(),
        ObjectType.GOAL,
        ObjectType.TASK,
        'from',
        100
      );

      // Fetch the actual task data for linked tasks
      const taskIds = relationships.fromObjects.map(obj => obj.id);
      const linkedTasksData: Task[] = [];
      
      for (const taskId of taskIds) {
        try {
          const response = await taskService.getTaskById(taskId.toString());
          if (response.success && response.task) {
            linkedTasksData.push(response.task);
          }
        } catch (err) {
          console.warn(`Failed to fetch task ${taskId}:`, err);
        }
      }
      
      setGoalTasks(linkedTasksData);
      
      // Recalculate progress after loading tasks
      await calculateProgressFromBackend();
    } catch (error) {
      console.error('Error loading linked tasks:', error);
    }
  };

  // Load sub-goals (goals with this goal as parent) via relationships
  const loadSubGoals = async () => {
    if (!user || !goal) return;
    
    try {
      const goalId = parseInt(goal.id);
      if (isNaN(goalId)) return;

      // Get relationships from this goal to other goals with parent_child relationship type
      const relationships = await relationshipService.getRelatedObjects(
        goalId.toString(),
        ObjectType.GOAL,
        ObjectType.GOAL,
        'from',
        100
      );

      // Filter for parent_child relationships and fetch the actual goal data
      const subGoalIds = relationships.fromObjects
        .filter(obj => obj.relationship.relationship_type === 'parent_child')
        .map(obj => obj.id);
      
      const subGoalsData: Goal[] = [];
      
      for (const subGoalId of subGoalIds) {
        try {
          const response = await goalService.getGoalById(subGoalId);
          if (response.success && response.goal) {
            const convertedGoal = goalService.convertFirebaseGoal(response.goal);
            subGoalsData.push(convertedGoal);
          }
        } catch (err) {
          console.warn(`Failed to fetch sub-goal ${subGoalId}:`, err);
        }
      }
      
      setSubGoals(subGoalsData);
    } catch (error) {
      console.error('Error loading sub-goals:', error);
    }
  };

  // Load all available goals for linking as sub-goals
  const loadAvailableGoals = async () => {
    if (!user || !goal) return;
    
    try {
      const response = await goalService.getUserGoals({
        user_id: user.id.toString(),
        limit: 100
      });
      
      if (response.success && response.goals) {
        // Filter out the current goal and already linked sub-goals
        const currentGoalId = parseInt(goal.id);
        const linkedSubGoalIds = subGoals.map(sg => parseInt(sg.id));
        
        const filteredGoals = response.goals
          .map(g => goalService.convertFirebaseGoal(g))
          .filter(g => {
            const goalIdNum = parseInt(g.id);
            return goalIdNum !== currentGoalId && !linkedSubGoalIds.includes(goalIdNum);
          });
        
        setAvailableGoals(filteredGoals);
      }
    } catch (error) {
      console.error('Error loading available goals:', error);
    }
  };

  // Refresh available goals when sub-goals change
  useEffect(() => {
    if (goal && user) {
      loadAvailableGoals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subGoals, goal?.id, user?.id]);

  // Load time logs for this goal
  const loadGoalTimeLogs = async () => {
    if (!user || !goal) return;
    
    try {
      const response = await TimeLogsService.getTimeLogsForActivity(user.id, 'goal', goal.id);
      setGoalTimeLogs(response);
      
      // Calculate statistics
      const totalMinutes = response.reduce((total, log) => total + log.duration_minutes, 0);
      const today = TimeLogsService.getTodayDate();
      const todayLogs = response.filter(log => 
        new Date(log.start_time).toISOString().split('T')[0] === today
      );
      const todayMinutes = todayLogs.reduce((total, log) => total + log.duration_minutes, 0);
      
      // Get this week's logs
      const thisWeekLogs = response.filter(log => TimeLogsService.isThisWeek(log));
      const thisWeekMinutes = thisWeekLogs.reduce((total, log) => total + log.duration_minutes, 0);
      
      setTimeLogStats({
        totalMinutes,
        totalHours: Math.round((totalMinutes / 60) * 100) / 100,
        sessionCount: response.length,
        todayMinutes,
        thisWeekMinutes
      });
    } catch (error) {
      console.error('Error loading goal time logs:', error);
    }
  };

  // Load metric value for this goal
  const loadMetricValue = async () => {
    if (!user || !goal || !goal.metric_name) {
      setCurrentMetricValue(null);
      setMetricProgress(0);
      return;
    }

    try {
      setMetricLoading(true);
      const response = await metricService.calculateMetric(goal.metric_name, user.id);
      
      if (response.success && response.metric) {
        setCurrentMetricValue(response.metric);
        
        // Calculate progress if target is set
        if (goal.target_metric_value) {
          const progress = goalService.calculateMetricProgress(
            response.metric.value,
            goal.target_metric_value
          );
          setMetricProgress(progress);
          
          // Update goal's current metric value in backend
          await goalService.updateStartingMetricValue(goal.id, response.metric.value);
        }
      }
    } catch (error) {
      console.error('Error loading metric value:', error);
      setCurrentMetricValue(null);
      setMetricProgress(0);
    } finally {
      setMetricLoading(false);
    }
  };

  // Calculate progress using backend API
  const calculateProgressFromBackend = useCallback(async () => {
    if (!goal?.id || !user) return;
    
    try {
      setProgressLoading(true);
      const response = await goalService.calculateGoalProgress(goal.id);
      
      if (response.success && response.progress) {
        setCalculatedProgress(prev => {
          // Check if backend indicates there are subgoals
          const hasSubgoalsInBackend = response.progress.breakdown?.subGoals !== null && response.progress.breakdown?.subGoals !== undefined;
          
          // If we have locally calculated progress based on subgoals AND backend also has subgoals, ignore this stale backend response
          // But if backend says there are no subgoals (null), we should always trust it and reset
          if (prev.basedOnSubgoals && hasSubgoalsInBackend) {
            return prev;
          }

          return {
            ...prev,
            overall: response.progress.overall,
            habitProgress: response.progress.habitProgress || 0,
            taskProgress: response.progress.taskProgress || 0,
            metricProgress: response.progress.metricProgress || 0,
            basedOnMetric: response.progress.basedOnMetric || false,
            basedOnSubgoals: hasSubgoalsInBackend,
            metricDetails: response.progress.metricDetails || null,
            breakdown: {
              ...prev.breakdown,
              habits: response.progress.breakdown.habits,
              tasks: response.progress.breakdown.tasks,
              metric: response.progress.breakdown.metric || null,
              subgoals: response.progress.breakdown.subGoals || null,
            }
          };
        });
      }
    } catch (error) {
      console.error('Error calculating progress:', error);
      // Fallback to default values
      setCalculatedProgress(prev => ({
        ...prev,
        overall: 0,
        habitProgress: 0,
        taskProgress: 0,
        metricProgress: 0,
        basedOnMetric: false,
        basedOnSubgoals: false,
        metricDetails: null,
        breakdown: {
          ...prev.breakdown,
          habits: { score: 0, weight: 0.5, contribution: 0, count: 0, details: [] },
          tasks: { score: 0, weight: 0.5, contribution: 0, count: 0, details: [] },
          metric: null,
          subgoals: null,
        }
      }));
    } finally {
      setProgressLoading(false);
    }
  }, [goal?.id, user]);

  const handleProgressUpdate = useCallback(async (newProgress: number) => {
    if (!goal) return;
    
    try {
      const result = await goalService.updateGoalProgress(goal.id, newProgress);
      
      if (result.success) {
        const updatedGoal = {
          ...goal,
          progress: newProgress,
          status: result.completed ? GoalStatus.COMPLETED : goal.status
        } as Goal;
        setGoal(updatedGoal);
        if (onGoalUpdate) {
          onGoalUpdate(updatedGoal);
        }
      } else {
        throw new Error('Failed to update progress');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update progress');
      console.error('Error updating progress:', err);
    } finally {
      setProgressLoading(false);
    }
  }, [goal, onGoalUpdate]);

  // Calculate progress when goal changes or subgoals are loaded
  useEffect(() => {
    if (subGoals.length > 0) {
      const totalProgress = subGoals.reduce((sum, sg) => {
        const progressVal = typeof sg.progress === 'string' ? parseFloat(sg.progress) : (sg.progress || 0);
        return sum + progressVal;
      }, 0);
      const averageProgress = totalProgress / subGoals.length;
      const roundedProgress = Math.round(averageProgress);

      const breakdownDetails = subGoals.map(sg => ({
        id: sg.id,
        title: sg.title,
        progress: typeof sg.progress === 'string' ? parseFloat(sg.progress) : (sg.progress || 0),
      }));

      setCalculatedProgress(prev => ({
        ...prev,
        overall: roundedProgress,
        basedOnSubgoals: true,
        basedOnMetric: false, // Subgoals progress overrides metric-based progress
        breakdown: {
          ...prev.breakdown,
          habits: { ...prev.breakdown.habits, weight: 0, contribution: 0 },
          tasks: { ...prev.breakdown.tasks, weight: 0, contribution: 0 },
          subgoals: {
            score: averageProgress,
            contribution: 100,
            weight: 1,
            count: subGoals.length,
            details: breakdownDetails,
          },
        },
      }));

      // Sync with backend if different
      if (goal && Math.round(goal.progress) !== roundedProgress) {
        handleProgressUpdate(roundedProgress);
      }

    } else if (goal?.id) {
      calculateProgressFromBackend();
    }
  }, [goal, subGoals, handleProgressUpdate, calculateProgressFromBackend]);

  const handleClose = () => {
    if (isRoutedPage) {
      navigate(-1); // Go back to previous URL
    } else if (onClose) {
      onClose();
    }
  };

  const handleGoalDeleteWrapper = async (goalId: string) => {
    try {
      if (onGoalDelete) {
        // Used as embedded component with delete handler
        await onGoalDelete(goalId);
        if (isRoutedPage) {
          navigate('/goals');
        }
      } else if (isRoutedPage) {
        // Handle deletion directly when used as routed page
        const result = await goalService.deleteGoal(goalId);
        
        if (result.success) {
          navigate('/goals');
        } else {
          throw new Error(result.message || 'Failed to delete goal');
        }
      }
    } catch (err: any) {
      console.error('Error deleting goal:', err);
      setError(err.message || 'Failed to delete goal');
      throw err; // Re-throw to be caught by handleDeleteGoal
    }
  };

  // Handle edit navigation
  const handleEditGoal = () => {
    console.log('handleEditGoal called', { isRoutedPage, goalId: goal?.id, hasOnGoalEdit: !!onGoalEdit });
    if (isRoutedPage && goal) {
      console.log('Navigating to edit page:', `/goals/${goal.id}/edit`);
      navigate(`/goals/${goal.id}/edit`, { replace: true });
    } else if (onGoalEdit && goal) {
      console.log('Calling onGoalEdit callback');
      onGoalEdit(goal);
    } else {
      console.warn('No edit handler available or goal not found');
    }
  };



  const handleCreateHabit = async () => {
    if (!habitFormData.title.trim() || !user) {
      setError('Habit title is required');
      return;
    }

    try {
      setLoading(true);
      
      // Create habit in the backend
      const habitData = {
        user_id: user.id,
        name: habitFormData.title,
        description: habitFormData.description,
        frequency_type: habitFormData.frequency,
        frequency_value: habitFormData.targetCount,
        tracking_type: 'count',
        target_count: habitFormData.targetCount,
        is_active: true,
        start_date: new Date().toISOString().split('T')[0]
      };

      const response = await habitService.createHabit(habitData);
      
      if (response.success && response.id) {
        // Create relationship between goal and habit
        const goalId = goal ? parseInt(goal.id) : NaN;
        if (!isNaN(goalId)) {
          await relationshipService.createObjectRelationship(
            goalId.toString(),
            ObjectType.GOAL,
            response.id.toString(),
            ObjectType.HABIT,
            'Goal-related habit'
          );
        }

        // Refresh linked habits
        await loadLinkedHabits();
        await loadAvailableHabits();
        
        setShowHabitForm(false);
        setHabitFormData({
          title: '',
          description: '',
          frequency: HabitFrequency.DAILY,
          targetCount: 1
        });
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to create habit');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create habit');
      console.error('Error creating habit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkHabits = async () => {
    if (!user || selectedHabitIds.length === 0) {
      setError('Please select at least one habit to link');
      return;
    }

    try {
      setLoading(true);
      console.log('Linking habits:', selectedHabitIds);
      console.log('Goal:', goal);
      const goalId = goal ? parseInt(goal.id) : NaN;
      if (isNaN(goalId)) {
        throw new Error('Invalid goal ID');
      }

      // Create relationships for selected habits
      for (const habitId of selectedHabitIds) {
        try {
          await relationshipService.createObjectRelationship(
            goalId.toString(),
            ObjectType.GOAL,
            habitId.toString(),
            ObjectType.HABIT,
            'Linked existing habit to goal'
          );
        } catch (err) {
          console.warn(`Failed to link habit ${habitId}:`, err);
        }
      }

      // Refresh linked habits
      await loadLinkedHabits();
      
      setShowLinkHabitModal(false);
      setSelectedHabitIds([]);
      setHabitSearchTerm('');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to link habits');
      console.error('Error linking habits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkHabit = async (habitId: number) => {
    if (!user) return;

    try {
      setLoading(true);
      const goalId = goal ? parseInt(goal.id) : NaN;
      if (isNaN(goalId)) {
        throw new Error('Invalid goal ID');
      }

      await relationshipService.deleteRelationshipByObjects(
        goalId,
        ObjectType.GOAL,
        habitId,
        ObjectType.HABIT
      );

      // Refresh linked habits
      await loadLinkedHabits();
    } catch (err: any) {
      setError(err.message || 'Failed to unlink habit');
      console.error('Error unlinking habit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHabitClick = (habitId: number) => {
    navigate(`/habits/${habitId}`);
  };

  const handleCreateSubGoal = async () => {
    if (!subGoalFormData.title.trim() || !subGoalFormData.description.trim() || !user) {
      setError('Sub-goal title and description are required');
      return;
    }

    try {
      setLoading(true);
      
      const parentGoalId = goal ? parseInt(goal.id) : NaN;
      if (isNaN(parentGoalId)) {
        throw new Error('Invalid parent goal ID');
      }

      // Create sub-goal without parent_goal_id
      const response = await goalService.createGoal(user.id.toString(), subGoalFormData);
      
      if (response.success && response.id) {
        // Create parent_child relationship
        await relationshipService.createObjectRelationship(
          parentGoalId.toString(),
          ObjectType.GOAL,
          response.id.toString(),
          ObjectType.GOAL,
          'Parent-child relationship',
          'parent_child'
        );

        // Refresh sub-goals list and available goals
        await loadSubGoals();
        await loadAvailableGoals();
        
        setShowSubGoalForm(false);
        setSubGoalFormData({
          title: '',
          description: '',
          category: GoalCategory.PERSONAL,
          targetDate: '',
          startedDate: '',
          notes: ''
        });
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to create sub-goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create sub-goal');
      console.error('Error creating sub-goal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkSubGoals = async () => {
    if (!user || selectedGoalIds.length === 0) {
      setError('Please select at least one goal to link');
      return;
    }

    try {
      setLoading(true);
      const parentGoalId = goal ? parseInt(goal.id) : NaN;
      if (isNaN(parentGoalId)) {
        throw new Error('Invalid parent goal ID');
      }

      // Create parent_child relationships for selected goals
      for (const subGoalId of selectedGoalIds) {
        try {
          await relationshipService.createObjectRelationship(
            parentGoalId.toString(),
            ObjectType.GOAL,
            subGoalId,
            ObjectType.GOAL,
            'Parent-child relationship',
            'parent_child'
          );
        } catch (err) {
          console.warn(`Failed to link goal ${subGoalId}:`, err);
        }
      }

      // Refresh sub-goals list and available goals
      await loadSubGoals();
      await loadAvailableGoals();
      
      setShowLinkSubGoalModal(false);
      setSelectedGoalIds([]);
      setGoalSearchTerm('');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to link goals');
      console.error('Error linking goals:', err);
    } finally {
      setLoading(false);
    }
  };

  // Task management functions
  const handleCreateTask = async () => {
    if (!taskFormData.title.trim() || !user) {
      setError('Task title is required');
      return;
    }

    try {
      setLoading(true);
      
      // Create task in the backend
      const response = await taskService.createTask(user.id.toString(), taskFormData);
      
      if (response.success && response.id) {
        // Create relationship between goal and task
        const goalId = goal ? goal.id : '';
        if (goalId !== '') {
          await relationshipService.createObjectRelationship(
            goalId.toString(),
            ObjectType.GOAL,
            response.id.toString(),
            ObjectType.TASK,
            'Goal-related task'
          );
        }

        // Refresh linked tasks
        await loadLinkedTasks();
        
        setShowTaskForm(false);
        setTaskFormData({
          title: '',
          description: '',
          status: 'todo',
          due_date: '',
          attachments: []
        });
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to create task');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create task');
      console.error('Error creating task:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId: number, newStatus: Task['status']) => {
    try {
      setLoading(true);
      
      const response = await taskService.updateTask({
        id: taskId.toString(),
        status: newStatus
      });
      
      if (response.success) {
        // Refresh linked tasks to show updated status
        await loadLinkedTasks();
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to update task status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update task status');
      console.error('Error updating task status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      setLoading(true);
      
      const response = await taskService.deleteTask(taskId.toString());
      
      if (response.success) {
        // Refresh linked tasks
        await loadLinkedTasks();
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to delete task');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete task');
      console.error('Error deleting task:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (task: Task) => {
    navigate(`/tasks/${task.id}`);
  };

  // Timer functions
  const handleStartTimer = () => {
    if (!user) return;
    
    const now = new Date();
    setTimerStartTime(now);
    setIsTimerRunning(true);
    setCurrentTimerDuration(0);
  };

  const handleStopTimer = async () => {
    if (!user || !timerStartTime) return;
    
    try {
      setLoading(true);
      const endTime = new Date();
      const duration = Math.floor((endTime.getTime() - timerStartTime.getTime()) / 1000 / 60); // minutes
      
      if (duration > 0) {
        const timeLogData: CreateTimeLogData = {
          user_id: user.id,
          activity_type: 'goal',
          activity_id: goal?.id || '',
          activity_name: goal?.title || '',
          duration_minutes: duration,
          start_time: timerStartTime.toISOString(),
          end_time: endTime.toISOString(),
          description: `Timer session for ${goal?.title || 'Goal'}`,
          is_manual_entry: false
        };

        await TimeLogsService.createTimeLog(timeLogData);
        await loadGoalTimeLogs();
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save time log');
      console.error('Error saving time log:', err);
    } finally {
      setLoading(false);
      setIsTimerRunning(false);
      setTimerStartTime(null);
      setCurrentTimerDuration(0);
    }
  };

  const handleCreateManualTimeLog = async () => {
    if (!user || !timeLogFormData.start_time || !timeLogFormData.end_time) {
      setError('Start time and end time are required');
      return;
    }

    try {
      setLoading(true);
      
      const timeLogData: CreateTimeLogData = {
        user_id: user.id,
        activity_type: 'goal',
        activity_id: goal?.id || '',
        activity_name: goal?.title || '',
        duration_minutes: timeLogFormData.duration_minutes,
        start_time: timeLogFormData.start_time,
        end_time: timeLogFormData.end_time,
        description: timeLogFormData.description || `Manual entry for ${goal?.title || 'Goal'}`,
        is_manual_entry: true
      };

      await TimeLogsService.createTimeLog(timeLogData);
      await loadGoalTimeLogs();
      
      setShowTimeLogForm(false);
      setTimeLogFormData({
        duration_minutes: 30,
        description: '',
        start_time: '',
        end_time: ''
      });
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to create time log');
      console.error('Error creating time log:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeLog = async (timeLogId: string) => {
    try {
      setLoading(true);
      await TimeLogsService.deleteTimeLog(timeLogId);
      await loadGoalTimeLogs();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete time log');
      console.error('Error deleting time log:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: GoalStatus) => {
    switch (status) {
      case GoalStatus.NOT_STARTED: return '#6c757d';
      case GoalStatus.IN_PROGRESS: return '#007bff';
      case GoalStatus.COMPLETED: return '#28a745';
      case GoalStatus.ON_HOLD: return '#ffc107';
      case GoalStatus.CANCELLED: return '#dc3545';
      default: return '#6c757d';
    }
  };

  const handleDeleteGoal = async () => {
    if (!goal) {
      console.warn('handleDeleteGoal called but goal is null');
      return;
    }
    
    console.log('handleDeleteGoal called for goal:', goal.id);
    
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      console.log('Calling handleGoalDeleteWrapper...');
      await handleGoalDeleteWrapper(goal.id);
      console.log('Goal deleted successfully');
      // Note: No need to setLoading(false) or setShowDeleteConfirm(false) here
      // because we should navigate away after successful deletion
    } catch (err: any) {
      console.error('Error in handleDeleteGoal:', err);
      setError(err.message || 'Failed to delete goal');
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const renderOverview = () => (
    <div className="goal-overview">
      {/* Hero Section */}
      <div className="goal-hero">
        <p className="hero-description">
          {goal?.description || 'No description'}
        </p>
      </div>

      {/* Key Metrics Cards */}
      <div className="key-metrics-cards">
        <div className="metric-card metric-card-progress">
          <div className="metric-card-value">{calculatedProgress.overall}%</div>
          <div className="metric-card-label">Progress</div>
          <div className="metric-card-bar">
            <div 
              className="metric-card-bar-fill"
              style={{ width: `${calculatedProgress.overall}%` }}
            ></div>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-card-value">
            {goalTasks.filter(t => t.status === 'completed').length}/{goalTasks.length}
          </div>
          <div className="metric-card-label">Tasks</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-card-value">{linkedHabits.length}</div>
          <div className="metric-card-label">Habits</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-card-value">{TimeLogsService.formatDuration(timeLogStats.totalMinutes)}</div>
          <div className="metric-card-label">Time Tracked</div>
        </div>
      </div>


      {/* Quick Info Bar */}
      <div className="quick-info-bar">
        <div className="info-item">
          <span className="info-label">Status</span>
          <span 
            className="status-badge-inline"
            style={{ backgroundColor: getStatusColor(goal?.status || GoalStatus.NOT_STARTED) }}
          >
            {goal?.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        <div className="info-item">
          <span className="info-label">Category</span>
          <span className="info-value">{goal?.category.replace('_', ' ').toUpperCase()}</span>
        </div>
        {goal?.targetDate && (
          <div className="info-item">
            <span className="info-label">Target</span>
            <span className="info-value">{formatDate(goal.targetDate)}</span>
          </div>
        )}
        <div className="info-item">
          <span className="info-label">Created</span>
          <span className="info-value">{formatDate(goal?.createdDate)}</span>
        </div>
      </div>

      {/* Metric Tracking Section - Simplified */}
      {goal?.metric_name && currentMetricValue ? (
        <div className="metric-display-simple">
          <div className="metric-header-simple">
            <span className="metric-label">{goal.metric_name}</span>
            {metricLoading ? (
              <span className="metric-value-loading">Loading...</span>
            ) : (
              <span className="metric-value-simple">
                {metricService.formatMetricValue(currentMetricValue.value, currentMetricValue.unit)} 
                {goal.target_metric_value && ` / ${metricService.formatMetricValue(goal.target_metric_value, currentMetricValue.unit)}`}
              </span>
            )}
          </div>
          {goal.target_metric_value && (
            <div className="metric-bar-simple">
              <div 
                className="metric-bar-fill-simple"
                style={{ width: `${Math.min(metricProgress, 100)}%` }}
              ></div>
            </div>
          )}
        </div>
      ) : null}

      {/* Collapsible Progress Breakdown */}
      <div className="collapsible-section">
        <button 
          className="collapsible-toggle"
          onClick={() => setShowProgressBreakdown(!showProgressBreakdown)}
        >
          <span className="toggle-label">How is progress calculated?</span>
          <span className="toggle-arrow">{showProgressBreakdown ? '▼' : '▶'}</span>
        </button>
        
        {showProgressBreakdown && (
          <div className="collapsible-content">
            <div className="progress-breakdown">
          <h4>Progress Breakdown</h4>
          {progressLoading ? (
            <div className="progress-loading">
              <div className="loading-spinner"></div>
              <p>Calculating progress...</p>
            </div>
          ) : calculatedProgress.basedOnMetric ? (
            <div className="breakdown-grid">
              <div className="breakdown-item metric-based">
                <div className="breakdown-header">
                  <span className="breakdown-label">/metric</span>
                  <span className="breakdown-score">{calculatedProgress.metricProgress}%</span>
                </div>
                <div className="breakdown-bar">
                  <div 
                    className="breakdown-fill metric"
                    style={{ width: `${calculatedProgress.metricProgress}%` }}
                  ></div>
                </div>
                <div className="breakdown-details">
                  <span className="breakdown-weight">Weight: 100%</span>
                  <span className="breakdown-metric-name">Metric: {calculatedProgress.metricDetails?.name}</span>
                </div>
                <div className="metric-progress-note">
                  <p>📊 Progress is calculated based on the linked metric</p>
                  {calculatedProgress.metricDetails && (
                    <p className="metric-values-summary">
                      {metricService.formatMetricValue(calculatedProgress.metricDetails.currentValue, currentMetricValue?.unit || 'count')} / {metricService.formatMetricValue(calculatedProgress.metricDetails.targetValue, currentMetricValue?.unit || 'count')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : calculatedProgress.basedOnSubgoals && calculatedProgress.breakdown.subgoals ? (
            <div className="breakdown-grid">
              <div className="breakdown-item subgoals-based">
                <div className="breakdown-header">
                  <span className="breakdown-label">/subgoals</span>
                  <span className="breakdown-score">{Math.round(calculatedProgress.breakdown.subgoals.score)}%</span>
                </div>
                <div className="breakdown-bar">
                  <div
                    className="breakdown-fill subgoals"
                    style={{ width: `${calculatedProgress.breakdown.subgoals.score}%` }}
                  ></div>
                </div>
                <div className="breakdown-details">
                  <span className="breakdown-weight">Weight: 100%</span>
                  <span className="breakdown-contribution">Contribution: {Math.round(calculatedProgress.overall)}%</span>
                  <span className="breakdown-count">Linked: {calculatedProgress.breakdown.subgoals.count}</span>
                </div>
                <div className="subgoals-progress-note">
                  <p>📈 Progress is the average of {calculatedProgress.breakdown.subgoals.count} sub-goals.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="breakdown-grid">
              <div className="breakdown-item">
                <div className="breakdown-header">
                  <span className="breakdown-label">/habits</span>
                  <span className="breakdown-score">{calculatedProgress.habitProgress}%</span>
                </div>
                <div className="breakdown-bar">
                  <div 
                    className="breakdown-fill habits"
                    style={{ width: `${calculatedProgress.habitProgress}%` }}
                  ></div>
                </div>
                <div className="breakdown-details">
                  <span className="breakdown-weight">Weight: {Math.round(calculatedProgress.breakdown.habits.weight * 100)}%</span>
                  <span className="breakdown-contribution">Contribution: {Math.round(calculatedProgress.breakdown.habits.contribution)}%</span>
                  <span className="breakdown-count">Linked: {calculatedProgress.breakdown.habits.count}</span>
                </div>
              </div>

              <div className="breakdown-item">
                <div className="breakdown-header">
                  <span className="breakdown-label">/tasks</span>
                  <span className="breakdown-score">{calculatedProgress.taskProgress}%</span>
                </div>
                <div className="breakdown-bar">
                  <div 
                    className="breakdown-fill tasks"
                    style={{ width: `${calculatedProgress.taskProgress}%` }}
                  ></div>
                </div>
                <div className="breakdown-details">
                  <span className="breakdown-weight">Weight: {Math.round(calculatedProgress.breakdown.tasks.weight * 100)}%</span>
                  <span className="breakdown-contribution">Contribution: {Math.round(calculatedProgress.breakdown.tasks.contribution)}%</span>
                  <span className="breakdown-count">Linked: {calculatedProgress.breakdown.tasks.count}</span>
                </div>
              </div>
            </div>
          )}
            </div>

            {/* Progress Summary */}
            <div className="progress-summary">
              <div className="summary-stats">
                {calculatedProgress.basedOnMetric ? (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">Progress Source:</span>
                      <span className="summary-value">📊 Metric-based</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Metric:</span>
                      <span className="summary-value">{calculatedProgress.metricDetails?.name}</span>
                    </div>
                  </>
                ) : calculatedProgress.basedOnSubgoals && calculatedProgress.breakdown.subgoals ? (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">Progress Source:</span>
                      <span className="summary-value">📈 Sub-goals</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Sub-goals count:</span>
                      <span className="summary-value">{calculatedProgress.breakdown.subgoals.count}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">Linked Habits:</span>
                      <span className="summary-value">{calculatedProgress.breakdown.habits.count}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Completed Tasks:</span>
                      <span className="summary-value">{goalTasks.filter(t => t.status === 'completed').length}/{goalTasks.length}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Collapsible Details Section */}
      <div className="collapsible-section">
        <button 
          className="collapsible-toggle"
          onClick={() => setShowDetailsSection(!showDetailsSection)}
        >
          <span className="toggle-label">Additional Details</span>
          <span className="toggle-arrow">{showDetailsSection ? '▼' : '▶'}</span>
        </button>
        
        {showDetailsSection && (
          <div className="collapsible-content">
            <div className="details-grid">
              {goal?.startedDate && (
                <div className="detail-item">
                  <span className="detail-label">Started</span>
                  <span className="detail-value">{formatDate(goal.startedDate)}</span>
                </div>
              )}
              {goal?.notes && (
                <div className="detail-item-full">
                  <span className="detail-label">Notes</span>
                  <p className="detail-notes">{goal.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );



  const renderHabits = () => {
    const filteredAvailableHabits = availableHabits.filter(habit => {
      const isAlreadyLinked = linkedHabits.some(linked => linked.id === habit.id);
      const matchesSearch = habitSearchTerm === '' || 
        habit.name.toLowerCase().includes(habitSearchTerm.toLowerCase()) ||
        (habit.description && habit.description.toLowerCase().includes(habitSearchTerm.toLowerCase()));
      return !isAlreadyLinked && matchesSearch;
    });

    return (
      <div className="goal-habits">
        <div className="section-header">
          <h3>/habits</h3>
          <div className="habit-actions">
            <button className="add-btn" onClick={() => setShowLinkHabitModal(true)}>
              🔗 Link Existing
            </button>
            <button className="add-btn" onClick={() => setShowHabitForm(true)}>
              + Create New
            </button>
          </div>
        </div>

        {linkedHabits.length > 0 ? (
          <div className="habits-list">
            {linkedHabits.map(habit => (
              <div key={habit.id} className="habit-card linked-habit">
                <div 
                  className="habit-card-content clickable"
                  onClick={() => handleHabitClick(habit.id)}
                  title="Click to view habit details"
                >
                  <div className="habit-header">
                    <h4>{habit.name}</h4>
                    <div className="habit-controls">
                      <span className={`habit-status ${habit.is_active ? 'active' : 'inactive'}`}>
                        {habit.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  {habit.description && <p>{habit.description}</p>}
                  <div className="habit-stats">
                    <div className="stat">
                      <label>Category:</label>
                      <span>{habit.category || 'General'}</span>
                    </div>
                    <div className="stat">
                      <label>Frequency:</label>
                      <span>{habit.frequency_type?.toUpperCase() || 'DAILY'}</span>
                    </div>
                    <div className="stat">
                      <label>Target:</label>
                      <span>{habit.target_count || 1} {habit.tracking_type === 'measurement' ? habit.measurement_unit || 'units' : 'times'}</span>
                    </div>
                    <div className="stat">
                      <label>Created:</label>
                      <span>{new Date(habit.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="habit-card-actions">
                  <button 
                    className="unlink-btn" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnlinkHabit(habit.id);
                    }}
                    title="Unlink habit from goal"
                  >
                    🔗⛔
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No habits linked to this goal yet. Link existing habits or create new ones to support your goal achievement!</p>
          </div>
        )}

        {/* Link Existing Habits Modal */}
        {showLinkHabitModal && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) setShowLinkHabitModal(false);
          }}>
            <div className="modal-content link-habits-modal">
              <div className="modal-header">
                <h3>Link Existing Habits</h3>
                <button className="close-btn" onClick={() => setShowLinkHabitModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="search-section">
                  <input
                    type="text"
                    placeholder="Search habits..."
                    value={habitSearchTerm}
                    onChange={(e) => setHabitSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                
                {filteredAvailableHabits.length > 0 ? (
                  <div className="available-habits-list">
                    {filteredAvailableHabits.map(habit => (
                      <div key={habit.id} className="habit-option">
                        <input
                          type="checkbox"
                          id={`habit-${habit.id}`}
                          checked={selectedHabitIds.includes(habit.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedHabitIds(prev => [...prev, habit.id]);
                            } else {
                              setSelectedHabitIds(prev => prev.filter(id => id !== habit.id));
                            }
                          }}
                        />
                        <label htmlFor={`habit-${habit.id}`} className="habit-option-label">
                          <div className="habit-info">
                            <h5>{habit.name}</h5>
                            {habit.description && <p>{habit.description}</p>}
                            <div className="habit-meta">
                              <span>Category: {habit.category || 'General'}</span>
                              <span>Frequency: {habit.frequency_type?.toUpperCase() || 'DAILY'}</span>
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>{habitSearchTerm ? 'No habits match your search.' : 'All your habits are already linked to this goal.'}</p>
                  </div>
                )}
                
                <div className="form-actions">
                  <button type="button" onClick={() => setShowLinkHabitModal(false)}>Cancel</button>
                  <button 
                    type="button" 
                    onClick={handleLinkHabits}
                    disabled={selectedHabitIds.length === 0}
                  >
                    Link {selectedHabitIds.length} Habit{selectedHabitIds.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {showHabitForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowHabitForm(false);
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Habit</h3>
              <button className="close-btn" onClick={() => setShowHabitForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Habit Title</label>
                <input
                  type="text"
                  value={habitFormData.title}
                  onChange={(e) => setHabitFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter habit title..."
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={habitFormData.description}
                  onChange={(e) => setHabitFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the habit..."
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Frequency</label>
                <select
                  value={habitFormData.frequency}
                  onChange={(e) => setHabitFormData(prev => ({ ...prev, frequency: e.target.value as HabitFrequency }))}
                >
                  {Object.values(HabitFrequency).map(freq => (
                    <option key={freq} value={freq}>
                      {freq.charAt(0).toUpperCase() + freq.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Target Count</label>
                <input
                  type="number"
                  min="1"
                  value={habitFormData.targetCount}
                  onChange={(e) => setHabitFormData(prev => ({ ...prev, targetCount: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowHabitForm(false)}>Cancel</button>
                <button type="button" onClick={handleCreateHabit}>Add Habit</button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    );
  };

  const renderTasks = () => {
    const getTaskStatusIcon = (status: Task['status']) => {
      switch (status) {
        case 'completed': return '✅';
        case 'in_progress': return '🔄';
        case 'on_hold': return '⏸️';
        case 'cancelled': return '❌';
        default: return '◯';
      }
    };

    const getTaskStatusColor = (status: Task['status']) => {
      switch (status) {
        case 'todo': return '#6c757d';
        case 'in_progress': return '#007bff';
        case 'completed': return '#28a745';
        case 'on_hold': return '#ffc107';
        case 'cancelled': return '#dc3545';
        default: return '#6c757d';
      }
    };

    const isOverdue = (task: Task) => {
      if (!task.due_date || task.status === 'completed') return false;
      return new Date(task.due_date) < new Date();
    };

    return (
      <div className="goal-tasks">
        <div className="section-header">
          <h3>/tasks</h3>
          <button className="add-btn" onClick={() => setShowTaskForm(true)}>
            + Add Task
          </button>
        </div>

        {goalTasks.length > 0 ? (
          <div className="tasks-list-compact">
            {goalTasks.map(task => (
              <div 
                key={task.id} 
                className={`task-item-compact ${task.status} ${isOverdue(task) ? 'overdue' : ''}`}
              >
                <div className="task-item-main" onClick={() => handleTaskClick(task)}>
                  <div className="task-status-icon-compact">
                    {getTaskStatusIcon(task.status)}
                  </div>
                  <div className="task-content-compact">
                    <h4 className="task-title-compact">{task.title}</h4>
                    {task.description && (
                      <p className="task-description-compact">{task.description}</p>
                    )}
                    <div className="task-meta-compact">
                      {task.due_date && (
                        <span className={`task-due-date-compact ${isOverdue(task) ? 'overdue' : ''}`}>
                          Due: {formatDate(task.due_date)}
                        </span>
                      )}
                      {task.assigned_date && (
                        <span className="task-assigned-date-compact">
                          Assigned: {formatDate(task.assigned_date)}
                        </span>
                      )}
                      <span className="task-status-text-compact">{task.status.replace('_', ' ')}</span>
                    </div>
                  </div>
                </div>
                <div className="task-actions-compact">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      const newStatus = task.status === 'completed' ? 'todo' : 'completed';
                      handleUpdateTaskStatus(task.id, newStatus);
                    }}
                    className="toggle-status-btn-compact"
                    title={task.status === 'completed' ? 'Mark as incomplete' : 'Mark as complete'}
                  >
                    {task.status === 'completed' ? '↶' : '✓'}
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTask(task.id);
                    }}
                    className="delete-task-btn-compact"
                    title="Delete task"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No tasks linked to this goal yet. Add tasks to break down your goal into actionable items!</p>
          </div>
        )}

        {/* Create Task Modal */}
        {showTaskForm && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) setShowTaskForm(false);
          }}>
            <div className="modal-content">
              <div className="modal-header">
                <h3>Add New Task</h3>
                <button className="close-btn" onClick={() => setShowTaskForm(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Task Title</label>
                  <input
                    type="text"
                    value={taskFormData.title}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter task title..."
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={taskFormData.description}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the task..."
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={taskFormData.status}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, status: e.target.value as Task['status'] }))}
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={taskFormData.due_date}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>
                <div className="form-actions">
                  <button type="button" onClick={() => setShowTaskForm(false)}>Cancel</button>
                  <button type="button" onClick={handleCreateTask}>Add Task</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };


  const handleSubGoalClick = (subGoal: Goal) => {
    setSelectedSubGoal(subGoal);
    setShowSubGoalDetail(true);
  };

  const handleUnlinkSubGoal = async (subGoalId: string) => {
    if (!user || !goal) return;

    try {
      setLoading(true);
      const parentGoalId = goal.id;
      if (!parentGoalId) {
        throw new Error('Invalid parent goal ID');
      }

      const response = await goalService.unlinkSubGoal(parentGoalId, subGoalId);
      
      if (response.success) {
        // Refresh sub-goals list and available goals
        await loadSubGoals();
        await loadAvailableGoals();
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to unlink sub-goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to unlink sub-goal');
      console.error('Error unlinking sub-goal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubGoal = async (subGoalId: string) => {
    if (!user) return;

    try {
      setLoading(true);
      
      const response = await goalService.deleteGoal(subGoalId);
      
      if (response.success) {
        // Refresh sub-goals list and available goals
        await loadSubGoals();
        await loadAvailableGoals();
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to delete sub-goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete sub-goal');
      console.error('Error deleting sub-goal:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderSubGoals = () => {
    const filteredAvailableGoals = availableGoals.filter(g => {
      const matchesSearch = goalSearchTerm === '' || 
        g.title.toLowerCase().includes(goalSearchTerm.toLowerCase()) ||
        (g.description && g.description.toLowerCase().includes(goalSearchTerm.toLowerCase()));
      return matchesSearch;
    });

    return (
      <div className="goal-subgoals">
        <div className="section-header">
          <h3>/subgoals</h3>
          <div className="subgoal-actions">
            <button className="add-btn" onClick={() => setShowLinkSubGoalModal(true)}>
              🔗 Link Existing
            </button>
            <button className="add-btn" onClick={() => setShowSubGoalForm(true)}>
              + Create New
            </button>
          </div>
        </div>

      <GoalList
        goals={subGoals}
        loading={loading}
        onGoalClick={handleSubGoalClick}
        onAddGoal={() => setShowSubGoalForm(true)}
        showAddButton={true}
        emptyStateTitle="No sub-goals yet"
        emptyStateMessage="Break down your main goal into smaller, manageable sub-goals!"
        emptyStateButtonText="Create Your First Sub-Goal"
      />

      {showSubGoalForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowSubGoalForm(false);
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Sub Goal</h3>
              <button className="close-btn" onClick={() => setShowSubGoalForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Sub Goal Title</label>
                <input
                  type="text"
                  value={subGoalFormData.title}
                  onChange={(e) => setSubGoalFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter sub goal title..."
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={subGoalFormData.description}
                  onChange={(e) => setSubGoalFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the sub goal..."
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={subGoalFormData.category}
                  onChange={(e) => setSubGoalFormData(prev => ({ ...prev, category: e.target.value as GoalCategory }))}
                >
                  {Object.values(GoalCategory).map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Started Date</label>
                <input
                  type="date"
                  value={subGoalFormData.startedDate || ''}
                  onChange={(e) => setSubGoalFormData(prev => ({ ...prev, startedDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Target Date</label>
                <input
                  type="date"
                  value={subGoalFormData.targetDate || ''}
                  onChange={(e) => setSubGoalFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={subGoalFormData.notes || ''}
                  onChange={(e) => setSubGoalFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes for this sub goal..."
                  rows={2}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowSubGoalForm(false)}>Cancel</button>
                <button type="button" onClick={handleCreateSubGoal} disabled={loading}>
                  {loading ? 'Creating...' : 'Add Sub Goal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Existing Goals Modal */}
      {showLinkSubGoalModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowLinkSubGoalModal(false);
        }}>
          <div className="modal-content link-goals-modal">
            <div className="modal-header">
              <h3>Link Existing Goals as Sub-Goals</h3>
              <button className="close-btn" onClick={() => setShowLinkSubGoalModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="search-section">
                <input
                  type="text"
                  placeholder="Search goals..."
                  value={goalSearchTerm}
                  onChange={(e) => setGoalSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              
              {filteredAvailableGoals.length > 0 ? (
                <div className="available-goals-list">
                  {filteredAvailableGoals.map(availableGoal => (
                    <div key={availableGoal.id} className="goal-option">
                      <input
                        type="checkbox"
                        id={`goal-${availableGoal.id}`}
                        checked={selectedGoalIds.includes(availableGoal.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGoalIds(prev => [...prev, availableGoal.id]);
                          } else {
                            setSelectedGoalIds(prev => prev.filter(id => id !== availableGoal.id));
                          }
                        }}
                      />
                      <label htmlFor={`goal-${availableGoal.id}`} className="goal-option-label">
                        <div className="goal-info">
                          <h5>{availableGoal.title}</h5>
                          {availableGoal.description && <p>{availableGoal.description}</p>}
                          <div className="goal-meta">
                            <span>Status: {availableGoal.status.replace('_', ' ').toUpperCase()}</span>
                            <span>Category: {availableGoal.category.replace('_', ' ').toUpperCase()}</span>
                            {availableGoal.progress !== undefined && (
                              <span>Progress: {availableGoal.progress}%</span>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>{goalSearchTerm ? 'No goals match your search.' : 'No available goals to link. All your goals are already linked as sub-goals.'}</p>
                </div>
              )}
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowLinkSubGoalModal(false)}>Cancel</button>
                <button 
                  type="button" 
                  onClick={handleLinkSubGoals}
                  disabled={selectedGoalIds.length === 0}
                >
                  Link {selectedGoalIds.length} Goal{selectedGoalIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderTimeLogs = () => (
    <div className="goal-timelogs">
      <div className="section-header">
        <h3>/timer</h3>
        <div className="timelog-actions">
          <button className="add-btn" onClick={() => setShowTimeLogForm(true)}>
            + Manual Entry
          </button>
        </div>
      </div>

      {/* Timer Section */}
      <div className="timer-section">
        <div className="timer-display">
          <div className="timer-time">
            {isTimerRunning ? TimeLogsService.formatDuration(currentTimerDuration) : '00:00'}
          </div>
          <div className="timer-status">
            {isTimerRunning ? 'Running...' : 'Stopped'}
          </div>
        </div>
        <div className="timer-controls">
          {!isTimerRunning ? (
            <button className="timer-btn start-btn" onClick={handleStartTimer}>
              ▶️ Start Timer
            </button>
          ) : (
            <button className="timer-btn stop-btn" onClick={handleStopTimer}>
              ⏹️ Stop Timer
            </button>
          )}
        </div>
      </div>

      {/* Time Statistics */}
      <div className="timelog-stats">
        <div className="stat-card">
          <div className="stat-value">{TimeLogsService.formatDuration(timeLogStats.totalMinutes)}</div>
          <div className="stat-label">Total Time</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{timeLogStats.sessionCount}</div>
          <div className="stat-label">Sessions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{TimeLogsService.formatDuration(timeLogStats.todayMinutes)}</div>
          <div className="stat-label">Today</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{TimeLogsService.formatDuration(timeLogStats.thisWeekMinutes)}</div>
          <div className="stat-label">This Week</div>
        </div>
      </div>

      {/* Time Logs List */}
      <div className="timelogs-list">
        <h4>Recent Sessions</h4>
        {goalTimeLogs.length > 0 ? (
          <div className="timelogs-grid">
            {goalTimeLogs.slice(0, 10).map(timeLog => (
              <div key={timeLog.id} className="timelog-card">
                <div className="timelog-header">
                  <div className="timelog-duration">
                    {TimeLogsService.formatDuration(timeLog.duration_minutes)}
                  </div>
                  <div className="timelog-type">
                    {timeLog.is_manual_entry ? '📝 Manual' : '⏱️ Timer'}
                  </div>
                </div>
                <div className="timelog-details">
                  <div className="timelog-date">
                    {TimeLogsService.formatDate(timeLog.start_time)}
                  </div>
                  <div className="timelog-time">
                    {TimeLogsService.formatTime(timeLog.start_time)} - {TimeLogsService.formatTime(timeLog.end_time)}
                  </div>
                  {timeLog.description && (
                    <div className="timelog-description">
                      {timeLog.description}
                    </div>
                  )}
                </div>
                <div className="timelog-actions">
                  <button 
                    className="delete-btn small-btn" 
                    onClick={() => handleDeleteTimeLog(timeLog.timelog_id)}
                    title="Delete time log"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No time logs recorded yet. Start a timer or add a manual entry to track your time spent on this goal!</p>
          </div>
        )}
      </div>

      {/* Manual Time Log Form Modal */}
      {showTimeLogForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowTimeLogForm(false);
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Manual Time Entry</h3>
              <button className="close-btn" onClick={() => setShowTimeLogForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  value={timeLogFormData.start_time}
                  onChange={(e) => setTimeLogFormData(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  value={timeLogFormData.end_time}
                  onChange={(e) => setTimeLogFormData(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Duration (minutes)</label>
                <input
                  type="number"
                  min="1"
                  value={timeLogFormData.duration_minutes}
                  onChange={(e) => setTimeLogFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={timeLogFormData.description}
                  onChange={(e) => setTimeLogFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what you worked on..."
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowTimeLogForm(false)}>Cancel</button>
                <button type="button" onClick={handleCreateManualTimeLog}>Add Time Entry</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderNotes = () => {
    if (noteLoading) {
      return (
        <div className="note-loading">
          <div className="loading-spinner"></div>
          <p>Loading goal notes...</p>
        </div>
      );
    }

    return (
      <BlockEditor
        blockManager={blockManager}
        blocks={contentBlocks}
        placeholder={`Write your thoughts about "${goal?.title || 'this goal'}"...`}
        onSyncStatusChange={() => {}} // Sync status is handled by hook
      />
    );
  };

  const renderHistory = () => {
    if (historyLoading) {
      return (
        <div className="history-loading">
          <div className="loading-spinner"></div>
          <p>Loading progress history...</p>
        </div>
      );
    }

    // Prepare chart data - reverse to show oldest first for chart
    const chartData = [...progressHistory].reverse().map(record => ({
      date: new Date(record.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: record.snapshot_date,
      progress: Math.round(record.progress_percentage * 100) / 100,
      habitProgress: record.habit_progress !== null ? Math.round(record.habit_progress * 100) / 100 : null,
      taskProgress: record.task_progress !== null ? Math.round(record.task_progress * 100) / 100 : null,
    }));

    // Check if we have habit or task progress data
    const hasHabitProgress = progressHistory.some(r => r.habit_progress !== null);
    const hasTaskProgress = progressHistory.some(r => r.task_progress !== null);

    return (
      <div className="goal-history">
        <div className="section-header">
          <h3>/history</h3>
        </div>

        {progressHistory.length === 0 ? (
          <div className="empty-state">
            <p>No progress history available yet. Progress snapshots are created daily.</p>
          </div>
        ) : (
          <>
            {/* Chart Section */}
            <div className="history-chart">
              <div className="history-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#666666"
                      tick={{ fill: '#666666', fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      domain={[0, 100]}
                      stroke="#666666"
                      tick={{ fill: '#666666', fontSize: 11 }}
                      label={{ value: 'Progress %', angle: -90, position: 'insideLeft', fill: '#666666' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '0.25rem',
                        color: '#333333'
                      }}
                      formatter={(value: number) => `${value}%`}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '1rem' }}
                      iconType="line"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="progress" 
                      stroke="#333333" 
                      strokeWidth={2}
                      dot={{ fill: '#333333', r: 3 }}
                      name="Overall Progress"
                    />
                    {hasHabitProgress && (
                      <Line 
                        type="monotone" 
                        dataKey="habitProgress" 
                        stroke="#666666" 
                        strokeWidth={1.5}
                        strokeDasharray="5 5"
                        dot={{ fill: '#666666', r: 2 }}
                        name="Habit Progress"
                      />
                    )}
                    {hasTaskProgress && (
                      <Line 
                        type="monotone" 
                        dataKey="taskProgress" 
                        stroke="#999999" 
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={{ fill: '#999999', r: 2 }}
                        name="Task Progress"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* History List Section */}
            <div className="history-list">
              <h4>Progress History</h4>
              <div className="history-table">
                <div className="history-table-header">
                  <div className="history-table-cell">Date</div>
                  <div className="history-table-cell">Overall</div>
                  {hasHabitProgress && <div className="history-table-cell">Habits</div>}
                  {hasTaskProgress && <div className="history-table-cell">Tasks</div>}
                  <div className="history-table-cell">Method</div>
                  {goal?.metric_name && <div className="history-table-cell">Metric</div>}
                </div>
                <div className="history-table-body">
                  {progressHistory.map((record, index) => (
                    <div key={index} className="history-table-row">
                      <div className="history-table-cell">
                        {formatDate(record.snapshot_date)}
                      </div>
                      <div className="history-table-cell">
                        <span className="progress-value">
                          {Math.round(record.progress_percentage * 100) / 100}%
                        </span>
                      </div>
                      {hasHabitProgress && (
                        <div className="history-table-cell">
                          {record.habit_progress !== null 
                            ? `${Math.round(record.habit_progress * 100) / 100}%`
                            : '-'}
                        </div>
                      )}
                      {hasTaskProgress && (
                        <div className="history-table-cell">
                          {record.task_progress !== null 
                            ? `${Math.round(record.task_progress * 100) / 100}%`
                            : '-'}
                        </div>
                      )}
                      <div className="history-table-cell">
                        <span className="method-badge">
                          {record.calculation_method === 'metric' ? '📊 Metric' : '📈 Habits/Tasks'}
                        </span>
                      </div>
                      {goal?.metric_name && (
                        <div className="history-table-cell">
                          {record.current_metric_value !== null && record.target_metric_value !== null
                            ? `${record.current_metric_value} / ${record.target_metric_value}`
                            : '-'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Early returns for routed page states
  if (isRoutedPage && !user) {
    return (
      <div className="App fullscreen-goals">
        <Sidebar
          user={user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar || (() => {})}
          onSignOut={onSignOut || (() => {})}
          onShowAuthModal={onShowAuthModal || (() => {})}
          authLoading={authLoading}
          currentPath="/goals"
        />
        <div className="goal-management">
          <div className="goal-header">
            <div className="header-left">
              <button className="menu-btn" onClick={onToggleSidebar || (() => {})}>☰</button>
            </div>
            <h1>Goal Details</h1>
          </div>
          <div className="auth-required">
            <p>Please sign in to view goal details.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isRoutedPage && loading) {
    return (
      <div className="App fullscreen-goals">
        <Sidebar
          user={user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar || (() => {})}
          onSignOut={onSignOut || (() => {})}
          onShowAuthModal={onShowAuthModal || (() => {})}
          authLoading={authLoading}
          currentPath="/goals"
        />
        <div className="goal-management">
          <div className="goal-header">
            <div className="header-left">
              <button className="menu-btn" onClick={onToggleSidebar || (() => {})}>☰</button>
            </div>
            <h1>Loading Goal...</h1>
          </div>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading goal details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isRoutedPage && (error || !goal)) {
    return (
      <div className="App fullscreen-goals">
        <Sidebar
          user={user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar || (() => {})}
          onSignOut={onSignOut || (() => {})}
          onShowAuthModal={onShowAuthModal || (() => {})}
          authLoading={authLoading}
          currentPath="/goals"
        />
        <div className="goal-management">
          <div className="goal-header">
            <div className="header-left">
              <button className="menu-btn" onClick={onToggleSidebar || (() => {})}>☰</button>
            </div>
            <h1>Goal Not Found</h1>
          </div>
          <div className="error-container">
            <p>{error || 'Goal not found'}</p>
            <button onClick={() => navigate('/goals')} className="back-to-goals-btn">
              Back to Goals
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!goal) {
    return <div>Loading...</div>;
  }

  const goalDetailContent = (
    <div className="goal-detail">
      <div className="goal-detail-header">
        <button className="back-btn" onClick={handleClose}>
          ←
        </button>
        <h1 className="goal-title">
          {goal?.title || 'Goal'}
        </h1>
        
        {/* Three-dot menu */}
        <div className="goal-options-menu">
          <button 
            className="options-menu-btn"
            onClick={() => setShowOptionsMenu(!showOptionsMenu)}
            title="More options"
          >
            ⋮
          </button>
          {showOptionsMenu && (
            <div className="options-menu-dropdown">
              <button 
                className="options-menu-item"
                onClick={(e) => {
                  console.log('Edit menu item clicked');
                  e.stopPropagation();
                  handleEditGoal();
                  setShowOptionsMenu(false);
                }}
              >
                ✏️ Edit Goal
              </button>
              <button 
                className="options-menu-item"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!goal) return;
                  
                  try {
                    setLoading(true);
                    setError(null);
                    // Calculate progress and get the result
                    const response = await goalService.calculateGoalProgress(goal.id);
                    
                    if (response.success && response.progress) {
                      const calculatedOverall = response.progress.overall || 0;
                      // Update the calculated progress state
                      setCalculatedProgress(prev => ({
                        ...prev,
                        overall: calculatedOverall,
                        habitProgress: response.progress.habitProgress || 0,
                        taskProgress: response.progress.taskProgress || 0,
                        metricProgress: response.progress.metricProgress || 0,
                        basedOnMetric: response.progress.basedOnMetric || false,
                        metricDetails: response.progress.metricDetails || null,
                        breakdown: {
                          ...prev.breakdown,
                          habits: response.progress.breakdown.habits,
                          tasks: response.progress.breakdown.tasks,
                          metric: response.progress.breakdown.metric || null
                        }
                      }));
                      // Update the goal's progress value in backend
                      await handleProgressUpdate(calculatedOverall);
                    } else {
                      throw new Error(response.message || 'Failed to calculate progress');
                    }
                    setShowOptionsMenu(false);
                  } catch (err: any) {
                    setError(err.message || 'Failed to update progress');
                    console.error('Error updating progress:', err);
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? 'Updating...' : '🔄 Update Progress'}
              </button>
              <button 
                className="options-menu-item delete-option"
                onClick={(e) => {
                  console.log('Delete menu item clicked');
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                  setShowOptionsMenu(false);
                }}
              >
                🗑️ Delete Goal
              </button>
            </div>
          )}
        </div>
        {/* Overlay to close menu when clicking outside */}
        {showOptionsMenu && (
          <div 
            className="options-menu-overlay" 
            onClick={() => setShowOptionsMenu(false)}
          />
        )}
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing...</p>
        </div>
      )}

      <div className="goal-detail-content">
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
/notes
          </button>

          <button 
            className={`tab-btn ${activeTab === 'habits' ? 'active' : ''}`}
            onClick={() => setActiveTab('habits')}
          >
/habits
          </button>
          <button 
            className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
/tasks
          </button>
          <button 
            className={`tab-btn ${activeTab === 'subgoals' ? 'active' : ''}`}
            onClick={() => setActiveTab('subgoals')}
          >
/subgoals
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
/history
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'habits' && renderHabits()}
          {activeTab === 'tasks' && renderTasks()}
          {activeTab === 'subgoals' && renderSubGoals()}
          {activeTab === 'notes' && renderNotes()}
          {activeTab === 'history' && renderHistory()}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowDeleteConfirm(false);
        }}>
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button className="close-btn" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the goal "{goal.title}"?</p>
              <p className="warning-text">This action cannot be undone and will also delete all related plans, habits, and sub-goals.</p>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowDeleteConfirm(false)} className="cancel-btn">
                Cancel
              </button>
              <button 
                type="button" 
                onClick={handleDeleteGoal} 
                className="delete-btn-confirm"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Goal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Goal Detail Modal */}
      {showSubGoalDetail && selectedSubGoal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowSubGoalDetail(false);
        }}>
          <div className="modal-content subgoal-detail-modal">
            <div className="modal-header">
              <h3>{selectedSubGoal.title}</h3>
              <button className="close-btn" onClick={() => setShowSubGoalDetail(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="subgoal-detail-content">
                <div className="detail-section">
                  <label>Description:</label>
                  <p>{selectedSubGoal.description}</p>
                </div>
                
                <div className="detail-section">
                  <label>Status:</label>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(selectedSubGoal.status) }}
                  >
                    {selectedSubGoal.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                <div className="detail-section">
                  <label>Progress:</label>
                  <div className="progress-display">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${selectedSubGoal.progress}%` }}
                      ></div>
                    </div>
                    <span>{selectedSubGoal.progress}%</span>
                  </div>
                </div>
                
                <div className="detail-section">
                  <label>Category:</label>
                  <span>{selectedSubGoal.category.replace('_', ' ').toUpperCase()}</span>
                </div>
                
                <div className="detail-section">
                  <label>Created:</label>
                  <span>{formatDate(selectedSubGoal.createdDate)}</span>
                </div>
                
                {selectedSubGoal.startedDate && (
                  <div className="detail-section">
                    <label>Started Date:</label>
                    <span>{formatDate(selectedSubGoal.startedDate)}</span>
                  </div>
                )}
                
                {selectedSubGoal.targetDate && (
                  <div className="detail-section">
                    <label>Target Date:</label>
                    <span>{formatDate(selectedSubGoal.targetDate)}</span>
                  </div>
                )}
                
                {selectedSubGoal.completedDate && (
                  <div className="detail-section">
                    <label>Completed:</label>
                    <span>{formatDate(selectedSubGoal.completedDate)}</span>
                  </div>
                )}
                
                {selectedSubGoal.notes && (
                  <div className="detail-section">
                    <label>Notes:</label>
                    <p>{selectedSubGoal.notes}</p>
                  </div>
                )}
              </div>
              
              <div className="subgoal-detail-actions">
                <button 
                  className="action-btn edit-btn" 
                  onClick={() => {
                    // TODO: Implement edit functionality
                    console.log('Edit sub-goal:', selectedSubGoal.id);
                  }}
                >
                  ✏️ Edit Sub-Goal
                </button>
                <button 
                  className="action-btn unlink-btn" 
                  onClick={() => {
                    handleUnlinkSubGoal(selectedSubGoal.id);
                    setShowSubGoalDetail(false);
                  }}
                >
                  🔗 Unlink Sub-Goal
                </button>
                <button 
                  className="action-btn delete-btn" 
                  onClick={() => {
                    handleDeleteSubGoal(selectedSubGoal.id);
                    setShowSubGoalDetail(false);
                  }}
                >
                  🗑️ Delete Sub-Goal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Return with or without sidebar wrapper based on usage
  if (isRoutedPage) {
    return (
      <div className="App fullscreen-goals">
        <Sidebar
          user={user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar || (() => {})}
          onSignOut={onSignOut || (() => {})}
          onShowAuthModal={onShowAuthModal || (() => {})}
          authLoading={authLoading}
          currentPath="/goals"
        />
        {goalDetailContent}
      </div>
    );
  }

  return goalDetailContent;
};

export default GoalDetail;
